<template>
  <div class="product-thumbnails-container" ref="thumbnailsVertical" :class="{ 'product-thumbnails-vertical': vertical }">
    <a @click="scrollThumbnailsLeft" class="product-thumbnails-arrow product-thumbnails-arrow-previous" :class="{ disabled: !thumbnailLeftEnabled }" :aria-label="'scroll thumbnail image left'">
      <span class="af af-chevron-left"></span>
    </a>
    <div class="product-thumbnails" :ref="thumbnails" v-if="isProductThumbnails">
      <a
        @click="$emit('clickThumbnail', index)"
        v-for="(media, index) in productMedias || []"
        :key="`product-${product?.hashKey ? product?.hashKey : media}-thumbnail-${index}`"
        class="product-thumbnail"
        :class="{ active: activeMedia === index }"
        :ref="setProductThumbnailRef"
      >
        <ResponsiveImage
          :lazy="false"
          :url="(media.type === 'VIDEO' ? media.thumbnailUrl : media.url) + '?fit=max&w={width}&q={q}'"
          :widths="[vertical ? 200 : 80]"
          :breakpoints="[]"
          :alt="`${media.alt} - Gallery View ${index + 1} of ${productMedias.length}.`"
        ></ResponsiveImage>
      </a>
    </div>
    <a @click="scrollThumbnailsRight" class="product-thumbnails-arrow product-thumbnails-arrow-next" :class="{ disabled: !thumbnailRightEnabled }" :aria-label="'scroll thumbnail image right'">
      ><span class="af af-chevron-right"></span>
    </a>
  </div>
</template>

<script>
import useResize from '@/compositions/useResize.js';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import { watch, ref, computed, onMounted, onUnmounted, onBeforeUpdate } from 'vue';
import VueScrollTo from 'vue-scrollto';

export default {
  name: 'ProductThumbnails',
  components: {
    ResponsiveImage,
  },

  props: {
    product: {
      default: null,
      type: Object,
    },
    activeMedia: {
      default: 0,
      type: [Number, String],
    },
    vertical: {
      default: false,
      type: Boolean,
    },
    medias: {
      default: null,
      type: Object,
    },
  },

  setup(props) {
    const { width } = useResize();
    const mounted = ref(false);
    const thumbnailLeftEnabled = ref(false);
    const thumbnailRightEnabled = ref(false);
    const thumbnails = ref(null);
    const thumbnailsVertical = ref(null);
    const productThumbnail = ref([]);
    const isProductThumbnails = computed(() => {
      return (props?.product && props?.product?.media) || props.medias;
    });
    const productMedias = computed(() => {
      return props?.product?.media.map(m=>({
        ...m,
        alt: `${props.product.title}`
      })) ??  props?.medias;
    });

    function setProductThumbnailRef(el) {
      if (el) {
        productThumbnail.value.push(el);
      }
    }

    onBeforeUpdate(() => {
      productThumbnail.value = [];
    });

    onMounted(() => {
      mounted.value = true;
      updateThumbnailArrows();
      if (thumbnails.value) {
        thumbnails.value.addEventListener('scroll', updateThumbnailArrows);
      }
    });

    onUnmounted(() => {
      if (thumbnails.value) {
        thumbnails.value.removeEventListener('scroll', updateThumbnailArrows);
      }
    });

    function updateThumbnailArrows() {
      if (thumbnails.value) {
        thumbnailLeftEnabled.value = thumbnails.value.scrollLeft > 0;
        thumbnailRightEnabled.value = thumbnails.value.scrollLeft + thumbnails.value.offsetWidth < thumbnails.value.scrollWidth;
      }
    }

    watch(width, updateThumbnailArrows);

    function scrollToThumbnail(index) {
      VueScrollTo.scrollTo(productThumbnail.value[index], {
        container: this.vertical ? thumbnailsVertical.value : thumbnails.value,
        offset: this.vertical ? 0 : -24,
        x: !this.vertical,
        y: this.vertical,
        force: false,
      });
    }

    function scrollThumbnailsLeft() {
      if (thumbnails.value) {
        thumbnails.value.scrollBy({
          left: -300,
          behavior: 'smooth',
        });
      }
    }

    function scrollThumbnailsRight() {
      if (thumbnails.value) {
        thumbnails.value.scrollBy({
          left: 300,
          behavior: 'smooth',
        });
      }
    }

    return {
      updateThumbnailArrows,
      mounted,
      thumbnailLeftEnabled,
      thumbnailRightEnabled,
      scrollToThumbnail,
      scrollThumbnailsLeft,
      scrollThumbnailsRight,
      thumbnails,
      thumbnailsVertical,
      productThumbnail,
      setProductThumbnailRef,
      isProductThumbnails,
      productMedias,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.product-thumbnails-container {
  position: relative;
  width: 100%;
}

.product-thumbnails {
  display: flex;
  width: 80px;
  min-width: 80px;
  min-height: 42px;
  overflow-x: scroll;
  width: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  border-bottom: 1px solid transparent;
  border-top: 1px solid var(--grey-ultra-light);

  @media (min-width: $tablet-breakpoint) {
    border-bottom: none;
    flex-wrap: wrap;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.product-thumbnails-arrow {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  border-top: 1px solid var(--grey-ultra-light);
  background-color: white;
  opacity: 1;
  transition: opacity 150ms ease-in-out;

  &.disabled {
    opacity: 0;
    pointer-events: none;
  }
}

.product-thumbnails-arrow-next {
  right: 0px;
  top: 0px;
  bottom: 0px;
}

.product-thumbnails-arrow-previous {
  left: 0px;
  top: 0px;
  bottom: 0px;
}

.product-thumbnail {
  width: 77px;
  min-width: 77px;
  margin-right: 1px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  display: inline-block;

  /* purgecss ignore */
  &:hover,
  &.active {
    border-bottom: 2px solid var(--coral);
  }
}

.product-thumbnails-vertical {
  overflow-y: scroll;

  .product-thumbnails {
    flex-direction: column;
    overflow-x: hidden;
    padding-bottom: var(--space-4);
  }

  .product-thumbnail {
    height: auto;
    width: calc(100% - var(--space-8));
    min-width: auto;
    margin-top: var(--space-4);
    margin-left: var(--space-4);
    margin-right: var(--space-4);
  }

  .product-thumbnails-arrow {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
  }

  .product-thumbnails-arrow-previous {
    right: 0px;
    top: 0px;
    bottom: auto;
  }

  .product-thumbnails-arrow-next {
    left: 0px;
    top: auto;
    bottom: 0px;
  }
}
</style>
